import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { baseUrl } from "../../helpers/constants";
import { getCookie, signout } from "../../helpers/cookies";

const customFetchBaseQuery = fetchBaseQuery({
    baseUrl: baseUrl,
    prepareHeaders: (headers, { getState }) => {
        const token = getCookie("magiclock_admin_token");
        if (token) {
            headers.set("Authorization", `BEARER ${token}`);
        }
        return headers;
    },
});

const baseQueryWithReauth = async (args, api, extraOptions) => {
    let result = await customFetchBaseQuery(args, api, extraOptions);

    if (result.error && result.error.status === 403) {
        signout(() => {
            window.location.href = "/login";
        });
    }

    return result;
};

const reportsApis = createApi({
    reducerPath: "reportsApis",
    baseQuery: baseQueryWithReauth,
    keepUnusedDataFor: 0,
    tagTypes: [
        "OrdersReport",
        "WarrantyReport",
        "InsuranceReport",
        "CustomersReport",
        "UsersReport",
        "SalesFunnelReport",
        "DashboardReport",
    ],
    endpoints: (build) => ({
        // Orders Report
        generateOrdersReport: build.query({
            query: (params) => {
                // Filter out null, empty, "null" string values, and internal flags
                const cleanParams = Object.fromEntries(
                    Object.entries(params || {}).filter(
                        ([key, value]) =>
                            value !== null &&
                            value !== "" &&
                            value !== "null" &&
                            value !== undefined &&
                            !key.startsWith("_"), // Exclude internal flags like _searchTriggered
                    ),
                );

                // Convert array parameters to comma-separated strings for URL params
                const processedParams = {};
                Object.entries(cleanParams).forEach(([key, value]) => {
                    if (Array.isArray(value) && value.length > 0) {
                        processedParams[key] = value.join(",");
                    } else if (!Array.isArray(value)) {
                        processedParams[key] = value;
                    }
                });

                return {
                    url: "/common/reports/orders",
                    method: "GET",
                    params: processedParams,
                };
            },
            transformResponse: (response) => response.result || response,
            providesTags: ["OrdersReport"],
            keepUnusedDataFor: 5,
        }),

        // Warranty Report
        generateWarrantyReport: build.query({
            query: (params) => {
                // Filter out null, empty, "null" string values, and internal flags
                const cleanParams = Object.fromEntries(
                    Object.entries(params || {}).filter(
                        ([key, value]) =>
                            value !== null &&
                            value !== "" &&
                            value !== "null" &&
                            value !== undefined &&
                            !key.startsWith("_"), // Exclude internal flags like _searchTriggered
                    ),
                );
                return {
                    url: "/common/reports/warranty",
                    method: "GET",
                    params: cleanParams,
                };
            },
            transformResponse: (response) => response.result || response,
            providesTags: ["WarrantyReport"],
            keepUnusedDataFor: 5,
        }),

        // Insurance Report
        generateInsuranceReport: build.query({
            query: (params) => {
                // Filter out null, empty, "null" string values, and internal flags
                const cleanParams = Object.fromEntries(
                    Object.entries(params || {}).filter(
                        ([key, value]) =>
                            value !== null &&
                            value !== "" &&
                            value !== "null" &&
                            value !== undefined &&
                            !key.startsWith("_"), // Exclude internal flags like _searchTriggered
                    ),
                );
                return {
                    url: "/common/reports/insurance",
                    method: "GET",
                    params: cleanParams,
                };
            },
            transformResponse: (response) => response.result || response,
            providesTags: ["InsuranceReport"],
            keepUnusedDataFor: 5,
        }),

        // Customers Report
        generateCustomersReport: build.query({
            query: (params) => {
                // Filter out null, empty, "null" string values, and internal flags
                const cleanParams = Object.fromEntries(
                    Object.entries(params || {}).filter(
                        ([key, value]) =>
                            value !== null &&
                            value !== "" &&
                            value !== "null" &&
                            value !== undefined &&
                            !key.startsWith("_"), // Exclude internal flags like _searchTriggered
                    ),
                );
                return {
                    url: "/common/reports/customers",
                    method: "GET",
                    params: cleanParams,
                };
            },
            transformResponse: (response) => response.result || response,
            providesTags: ["CustomersReport"],
            keepUnusedDataFor: 5,
        }),

        // Users Report
        generateUsersReport: build.query({
            query: (params) => {
                // Filter out null, empty, "null" string values, and internal flags
                const cleanParams = Object.fromEntries(
                    Object.entries(params || {}).filter(
                        ([key, value]) =>
                            value !== null &&
                            value !== "" &&
                            value !== "null" &&
                            value !== undefined &&
                            !key.startsWith("_"), // Exclude internal flags like _searchTriggered
                    ),
                );
                return {
                    url: "/common/reports/users",
                    method: "GET",
                    params: cleanParams,
                };
            },
            transformResponse: (response) => response.result || response,
            providesTags: ["UsersReport"],
            keepUnusedDataFor: 5,
        }),

        // Sales Funnel Report
        generateSalesFunnelReport: build.query({
            query: (params) => {
                // Filter out null, empty, "null" string values, and internal flags
                const cleanParams = Object.fromEntries(
                    Object.entries(params || {}).filter(
                        ([key, value]) =>
                            value !== null &&
                            value !== "" &&
                            value !== "null" &&
                            value !== undefined &&
                            !key.startsWith("_"), // Exclude internal flags like _searchTriggered
                    ),
                );
                return {
                    url: "/common/reports/sales-funnel",
                    method: "GET",
                    params: cleanParams,
                };
            },
            transformResponse: (response) => response.result || response,
            providesTags: ["SalesFunnelReport"],
            keepUnusedDataFor: 5,
        }),

        // Dashboard Report
        generateDashboardReport: build.query({
            query: (params) => {
                // Filter out null, empty, "null" string values, and internal flags
                const cleanParams = Object.fromEntries(
                    Object.entries(params || {}).filter(
                        ([key, value]) =>
                            value !== null &&
                            value !== "" &&
                            value !== "null" &&
                            value !== undefined &&
                            !key.startsWith("_"), // Exclude internal flags like _searchTriggered
                    ),
                );
                return {
                    url: "/common/reports/dashboard",
                    method: "GET",
                    params: cleanParams,
                };
            },
            transformResponse: (response) => response.result || response,
            providesTags: ["DashboardReport"],
            keepUnusedDataFor: 5,
        }),
    }),
});

export const {
    useGenerateOrdersReportQuery,
    useGenerateWarrantyReportQuery,
    useGenerateInsuranceReportQuery,
    useGenerateCustomersReportQuery,
    useGenerateUsersReportQuery,
    useGenerateSalesFunnelReportQuery,
    useGenerateDashboardReportQuery,
} = reportsApis;

export default reportsApis;
